// The four moves of a turn - roll, choose a play, stop, and acknowledge a
// bust - plus claiming columns and the end of the game. Everything is a pure
// function (state) -> newState; nothing mutates its input.

import {
  COLUMN_HEIGHTS, COLUMNS_TO_WIN, DICE_COUNT, PHASES, TURN_PHASES,
} from '../constants';
import { moveContext, nextStepIn, optionsForRoll } from '../moves';
import { freshTurn } from '../state/createGameState';
import { nextInt } from '../rng/rng';

const clone = (state) => ({
  ...state,
  meta: { ...state.meta, seq: (state.meta.seq || 0) + 1 },
  columns: { ...state.columns },
  turn: {
    ...state.turn,
    runners: { ...state.turn.runners },
    dice: state.turn.dice ? [...state.turn.dice] : null,
    options: state.turn.options ? state.turn.options.map((o) => ({ ...o, sums: [...o.sums] })) : null,
  },
  players: state.players.map((p) => ({ ...p, markers: { ...p.markers } })),
  log: [...state.log],
});

export const activePlayer = (state) => state.players[state.meta.activePlayerIndex];

/** How many columns this player has claimed. The win condition counts these. */
export const claimedCount = (state, playerId) => (
  Object.values(state.columns).filter((owner) => owner === playerId).length
);

export const canRoll = (state) => (
  state.meta.phase === PHASES.PLAYING && state.turn.phase === TURN_PHASES.ROLLING
);

/**
 * Stopping is offered only once something has moved this turn. The rulebook
 * offers the choice "after you finish placing or advancing the white cubes"
 * (p2, Choosing to stop) - before the first roll there is nothing to bank,
 * and a stop would just be a skipped turn nobody ever wants.
 */
export const canStop = (state) => (
  state.meta.phase === PHASES.PLAYING
  && state.turn.phase === TURN_PHASES.ROLLING
  && state.turn.moved
);

const passTurn = (next) => {
  next.meta.activePlayerIndex = (next.meta.activePlayerIndex + 1) % next.players.length;
  next.meta.turn += 1;
  next.turn = freshTurn();
};

/**
 * Roll all four dice and work out what they allow.
 *
 * If nothing is playable the turn is a bust - but the state parks in
 * `busted` rather than passing the seat immediately, so every screen gets to
 * show the roll that did it. ACK_BUST completes the fall.
 */
export const roll = (state) => {
  if (!canRoll(state)) return state;

  const next = clone(state);
  let { rngState } = next.meta;
  const dice = [];
  for (let i = 0; i < DICE_COUNT; i += 1) {
    const [value, after] = nextInt(6, rngState);
    rngState = after;
    dice.push(value + 1);
  }
  next.meta.rngState = rngState;
  next.turn.dice = dice;

  const options = optionsForRoll(dice, moveContext(next));
  const player = activePlayer(next);

  if (options.length === 0) {
    next.turn.phase = TURN_PHASES.BUSTED;
    next.turn.options = null;
    next.log.push({
      kind: 'bust', player: player.name, playerId: player.id, dice,
    });
    return next;
  }

  next.turn.phase = TURN_PHASES.CHOOSING;
  next.turn.options = options;
  next.log.push({
    kind: 'roll', player: player.name, playerId: player.id, dice,
  });
  return next;
};

/**
 * Apply one of the offered plays: advance a runner for each sum in it.
 *
 * A runner already in the column steps up one; a fresh runner enters "one
 * space ahead" of the player's own committed marker, "otherwise ... the
 * bottom space" (p1, Rolling) - both cases are nextStepIn. A doubled sum
 * ([8, 8]) simply runs the same advance twice.
 */
export const chooseMove = (state, optionIndex) => {
  if (state.meta.phase !== PHASES.PLAYING) return state;
  if (state.turn.phase !== TURN_PHASES.CHOOSING) return state;
  const option = state.turn.options?.[optionIndex];
  if (!option) return state;

  const next = clone(state);
  for (const sum of option.sums) {
    next.turn.runners[sum] = nextStepIn(sum, moveContext(next));
  }
  const player = activePlayer(next);
  next.log.push({
    kind: 'advance', player: player.name, playerId: player.id, sums: [...option.sums],
  });

  next.turn.phase = TURN_PHASES.ROLLING;
  next.turn.options = null;
  next.turn.moved = true;
  return next;
};

/**
 * Bank this turn's progress: white runners become the player's own markers.
 *
 * A runner standing on a column's top space claims that column - the
 * player's marker plants there for good and "all other players' cubes in
 * that column get returned to their owners" (p2, Claiming a column). Three
 * claimed columns wins on the spot; note the claim only happens on a STOP,
 * so a runner sitting on top is worth nothing until its player banks it.
 */
export const stop = (state) => {
  if (!canStop(state)) return state;

  const next = clone(state);
  const player = next.players[next.meta.activePlayerIndex];
  const claimedNow = [];

  for (const [columnKey, step] of Object.entries(next.turn.runners)) {
    const column = Number(columnKey);
    player.markers[column] = step;
    if (step >= COLUMN_HEIGHTS[column]) {
      next.columns[column] = player.id;
      claimedNow.push(column);
      // Everyone's markers leave a claimed column, the owner's included -
      // the claim itself is recorded on the board, not as a marker.
      next.players.forEach((p) => { delete p.markers[column]; });
    }
  }

  next.log.push({
    kind: 'stop',
    player: player.name,
    playerId: player.id,
    claimed: claimedNow,
  });

  if (claimedCount(next, player.id) >= COLUMNS_TO_WIN) {
    next.meta.phase = PHASES.FINISHED;
    next.meta.winnerId = player.id;
    next.turn = freshTurn();
    next.log.push({ kind: 'gameEnd', winner: player.name, winnerId: player.id });
    return next;
  }

  passTurn(next);
  return next;
};

/** The bust has been seen; the runners vanish and the seat passes left. */
export const ackBust = (state) => {
  if (state.meta.phase !== PHASES.PLAYING) return state;
  if (state.turn.phase !== TURN_PHASES.BUSTED) return state;

  const next = clone(state);
  passTurn(next);
  return next;
};
